import type { SxProps, Theme } from "@mui/material";
import type { FC } from "react";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useJobs } from "../../../hooks/useJobs";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import type { NurseRole } from "../../../utils/models";
import { JobSelector } from "./JobSelector";
import { RoleChips } from "./RoleChips";

type Props = {
	// Role mode
	selectedRoles: (NurseRole | "all")[];
	onSelectedRolesChanged: (selected: (NurseRole | "all")[]) => void;
	minimumOneSelected?: boolean;
	availableRoles?: NurseRole[];
	// Job mode
	selectedJobIds: string[];
	onSelectedJobsChanged: (selected: string[]) => void;
	/** Restrict the job dropdown to these job ids (e.g. only jobs that map to a coverage role-group).
	    Omit to offer every enabled job. Ignored in role-chip mode. */
	availableJobIds?: string[];
	// Job dropdown styling
	label?: string;
	formControlSx?: SxProps<Theme>;
	selectSx?: SxProps<Theme>;
};

/**
 * Picks role chips or a multi-select job dropdown based on the JobManagementEnabled flag.
 * Consumers maintain both selection states so toggling the flag preserves the inactive one.
 */
export const RoleOrJobSelector: FC<Props> = ({
	selectedRoles,
	onSelectedRolesChanged,
	minimumOneSelected,
	availableRoles,
	selectedJobIds,
	onSelectedJobsChanged,
	availableJobIds,
	label,
	formControlSx,
	selectSx,
}) => {
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const jobs = useJobs();
	const shownJobs = availableJobIds ? jobs.filter((j) => availableJobIds.includes(j.id)) : jobs;

	if (jobManagementEnabled && shownJobs.length > 0) {
		// JobManagement on but no jobs configured: fall back to role chips so consumers don't render an empty dropdown.
		return (
			<JobSelector
				variant="dropdown"
				jobs={shownJobs}
				selectedJobIds={selectedJobIds}
				onSelectedJobsChanged={onSelectedJobsChanged}
				label={label}
				formControlSx={formControlSx}
				selectSx={selectSx}
			/>
		);
	}

	return (
		<RoleChips
			onSelectedRoleFiltersChanged={onSelectedRolesChanged}
			initiallySelected={selectedRoles}
			minimumOneSelected={minimumOneSelected}
			availableRoles={availableRoles}
		/>
	);
};
